import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChevronDownIcon, ListFilter } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import AdvancedFilterBuilder from "./AdvancedFilterBuilder";
import FilterDropdown, { supportedFieldTypes } from "./FilterDropdown";
import { ContentTypes } from "../../../constants/action-types";

export type FilterRuleProps = {
  operator: string;
  fieldType: string;
  fieldId: string;
  fieldValue: (string | number)[];
};

export const defaultFilterRule: FilterRuleProps = {
  operator: "",
  fieldType: "",
  fieldId: "",
  fieldValue: [],
};

export const visibilityModes = [
  { value: "Private", label: "Private" },
  { value: "Shared", label: "Shared" },
];

const supportedFieldTaskView = ["summary", "assignees", "start"];

const supportedFieldNoteView = ["summary", "start"];

const supportedFieldEmailView = ["summary", "start"];

export const convertFiltersDataToSearchFilters = (datas: FilterRuleProps[]) => {
  const filters = {};
  datas.forEach((data) => {
    const operator = data.operator;
    const fieldType = data.fieldType;
    const fieldId = data.fieldId;
    const fieldValue = data.fieldValue;
    const values = filters[fieldId]?.values || [];
    if (operator === "dateRange") {
      if (values.length === 0) {
        fieldValue.forEach((value) => {
          values.push(value);
        });
      }
    } else {
      fieldValue.forEach((value) => {
        values.push(value);
      });
    }
    filters[fieldId] = {
      operator: operator,
      dataType: fieldType,
      values,
    };
  });
  return filters;
};

export const convertSearchFiltersToFiltersData = (filters: any) => {
  const datas: FilterRuleProps[] = [];
  Object.entries(filters.toJS()).forEach(([key, value]: any) => {
    const fieldValues = value.values || [];
    if (value.operator === "in" || value.operator === "dateRange") {
      datas.push({
        operator: value.operator,
        fieldType: value.dataType,
        fieldId: key,
        fieldValue: fieldValues,
      });
    } else {
      if (fieldValues.length > 0) {
        fieldValues.forEach((fieldValue) => {
          datas.push({
            operator: value.operator,
            fieldType: value.dataType,
            fieldId: key,
            fieldValue: [fieldValue],
          });
        });
      } else {
        datas.push({
          operator: value.operator,
          fieldType: value.dataType,
          fieldId: key,
          fieldValue: [],
        });
      }
    }
  });
  return datas.length > 0 ? datas : [];
};

interface AdvancedFilterProps {
  contentType: string;
  appView: string;
  filters: any;
  onChangeFilter: (filters: any) => void;
}

const AdvancedFilter = ({ contentType, appView, filters, onChangeFilter }: AdvancedFilterProps) => {
  const { t } = useTranslation();

  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));

  const availableFields = useMemo(() => {
    const fieldDatas = fields
      .filter(
        (item) =>
          item.get("formType") == contentType && supportedFieldTypes.includes(item.get("type"))
      )
      .map((item) => ({
        label: item.get("label"),
        value: item.get("id"),
        data: item.toJS(),
      }))
      .toJS();
    if (contentType === ContentTypes.ACTIVITY && appView === "Tasks") {
      return fieldDatas.filter((item) => supportedFieldTaskView.includes(item.value));
    } else if (contentType === ContentTypes.ACTIVITY && appView === "Notes") {
      return fieldDatas.filter((item) => supportedFieldNoteView.includes(item.value));
    } else if (
      contentType === ContentTypes.ACTIVITY &&
      (appView === "Drafts" || appView === "Outbox" || appView === "Tracked")
    ) {
      return fieldDatas.filter((item) => supportedFieldEmailView.includes(item.value));
    }
    return fieldDatas;
  }, [contentType, appView, fields]);

  const [filterRules, setFilterRules] = useState<FilterRuleProps[]>([]);

  useEffect(() => {
    if (filters) {
      setFilterRules(convertSearchFiltersToFiltersData(filters));
    }
  }, [filters]);

  const totalFilters = filterRules.filter((item) => (item.fieldValue || []).length > 0).length;

  const handleChange = (index: number, value: FilterRuleProps) => {
    const newFilterRules = filterRules.map((item, idx) => (idx === index ? value : item));
    setFilterRules([...newFilterRules]);
    if (value.fieldId && value.operator) {
      const newFilter = convertFiltersDataToSearchFilters(newFilterRules);
      onChangeFilter(newFilter);
    }
  };

  const handleAdd = (value) => {
    setFilterRules([...filterRules, value]);
    if (value.fieldId && value.operator && value.fieldValue && value.fieldValue.length > 0) {
      const newFilter = convertFiltersDataToSearchFilters([...filterRules, value]);
      onChangeFilter({
        ...newFilter,
        ...filters.toJS(),
      });
    }
  };

  const handleRemove = (index: number) => {
    const newFilterRules = filterRules.filter((_, idx) => idx !== index);
    setFilterRules([...newFilterRules]);
    const newFilter = convertFiltersDataToSearchFilters(newFilterRules);
    onChangeFilter(newFilter);
  };

  const handleDeleteAdvancedFilter = () => {
    setFilterRules([]);
    onChangeFilter({});
  };

  const renderPopoverPanel = (closePopover) => {
    if (filterRules.length === 0) {
      return (
        <FilterDropdown
          fields={availableFields}
          isFilter={true}
          onClick={(value) => {
            handleAdd(value);
          }}
          onClose={() => {}}
        />
      );
    }
    return (
      <AdvancedFilterBuilder
        availableFields={availableFields}
        filterRules={filterRules}
        onAdd={(value) => {
          handleAdd(value);
        }}
        onChange={handleChange}
        onRemove={(i) => {
          if (filterRules.length === 1) {
            closePopover();
          }
          handleRemove(i);
        }}
        onDeleteAdvancedFilter={() => {
          handleDeleteAdvancedFilter();
          closePopover();
        }}
      />
    );
  };

  return (
    <div>
      <Popover>
        <PopoverButton className="inline-flex items-center space-x-2 rounded-md h-8 px-2 py-1.5 text-sm font-medium text-gray-500 border border-gray-300 outline-none">
          <ListFilter className="h-3 w-3 text-gray-500" />
          <div className="pr-2">{t("Filter")}</div>
          {totalFilters > 0 ? (
            <div className="w-max px-1.5 py-0.5 rounded-full text-center text-xs text-white bg-gray-600">
              +{totalFilters}
            </div>
          ) : null}
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        </PopoverButton>
        <PopoverPanel
          anchor="bottom start"
          className="z-20 mt-2 p-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
        >
          {({ close }) => renderPopoverPanel(close)}
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default AdvancedFilter;
